import * as React from 'react'
import {VariantProps, cva} from 'class-variance-authority'
import {ClassValue, clsx} from 'clsx'
import {twMerge} from 'tailwind-merge'

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs))
}

const buttonVariants = cva(
  'items-center cursor-pointer flex justify-center group disabled:border disabled:text-gray-600 disabled:border-gray-600 disabled:cursor-not-allowed',
  {
    variants: {
      variant: {
        outline: 'border',
        ghost: '',
        noStyle: '',
      },
      size: {
        default: 'py-2 px-5 text-sm',
        xsm: 'py-1.5 px-3 text-sm',
        sm: 'h-8 w-16 text-sm',
        md: 'h-8 w-20 text-sm',
        xmd: 'py-2 px-8',
        lg: 'py-3 px-11',
        xlg: 'h-10 w-full text-xs',
        zero: 'py-0 px-0',
      },
      colorScheme: {
        solid:
          'text-dope-black bg-white hover:shadow-spread-inset-1.5 hover:bg-opacity-85',
        red: 'border-red-100 text-red-100 hover:bg-red-100 hover:bg-opacity-20',
        cyan: 'border-dope-cyan-bright text-dope-cyan-bright hover:bg-dope-cyan-bright hover:bg-opacity-30',
        white:
          'border-solid border-white hover:bg-white hover:bg-opacity-20 outline-none',
        gray: 'border-gray-400 hover:bg-white hover:text-black font-bold',
        noBackground: 'hover:bg-white hover:bg-opacity-20 outline-none',
        noStyle: '',
      },
      fontWeight: {
        default: 'karla',
        bold: 'karla-bold',
      },
      borderRadius: {
        rounded: 'rounded',
        roundedFull: 'rounded-l-full rounded-r-full',
      },
    },
    defaultVariants: {
      variant: 'outline',
      size: 'default',
      fontWeight: 'default',
      colorScheme: 'solid',
    },
  },
)

export interface ButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof buttonVariants> {}

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  (
    {
      className,
      children,
      colorScheme,
      variant,
      size,
      borderRadius,
      fontWeight,
      ...props
    },
    ref,
  ) => {
    return (
      <button
        className={cn(
          buttonVariants({
            variant,
            size,
            colorScheme,
            fontWeight,
            borderRadius,
            className,
          }),
        )}
        ref={ref}
        {...props}
      >
        {children}
      </button>
    )
  },
)
Button.displayName = 'Button'

export {Button}
